import { Autocomplete, TextField, InputLabel, Box, Typography, useMediaQuery } from "@mui/material";
import { Controller } from 'react-hook-form';
import { theme } from "../../../utils/theme/godzilla_light";
import ExpandMoreIcon from '@mui/icons-material/ArrowDropDown';

interface IAutoSearchProps {
    options: { label: string, id: string | number }[];
    noDataObj?: { heading: string, msg: string },
    sx?: any;
    label: string;
    inputStyle?: any;
    labelStyle?: any;
    required?: boolean;
    name: string,
    control: any,
    defaultValue: string,
    error: any
}

const styles = {
    inputTextStyle: {
        "& .MuiInputBase-root": {
            "& .MuiOutlinedInput-notchedOutline": {
                border: "1px solid #404144",
                borderRadius: "4px"
            },
            "&.Mui-error": {
                "& .MuiOutlinedInput-notchedOutline": {
                    border: '1px solid #404144'
                }
            },
            "&.Mui-focused": {
                "& .MuiOutlinedInput-notchedOutline": {
                    border: '1px solid #404144',
                    borderRadius: "4px"
                },
                "&.Mui-error": {
                    "& .MuiOutlinedInput-notchedOutline": {
                        border: '2px solid #E04A57',
                        borderRadius: "4px"
                    }
                }
            },
            "& .MuiInputBase-input": {
                padding: "0px",
                "&::placeholder": {
                    opacity: 1,
                    ...theme?.typography?.body1Regular
                },
            },
        },
        '& .MuiFormHelperText-root': {
            ...theme?.typography?.components?.helperText,
            mt: '4px',
            ml: 0,
            mr: 0,
            '&.Mui-error': {
                color: theme?.palette?.error?.dark,
            }
        }
    },
    emptyComponentWrapper: {
        width: "400px",
        maxWidth: "400px",
        py: "0.5rem"
    },
    emptyText: {
        ...theme?.typography?.body1Regular,
        textAlign: "left"
    }
};

const autocompleteFieldStyle = (width: string, height: string) => ({
    "& .MuiInputBase-root": {
        ...theme?.typography?.body1Regular,
        height: height,
        "& .MuiOutlinedInput-notchedOutline": {
            border: '1px solid #939597',
            borderRadius: "4px",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
            border: '1px solid #939597',
            borderRadius: "4px",
        },
        "&.Mui-error": {
            "& .MuiOutlinedInput-notchedOutline": {
                border: '1px solid #E04A57',
            }
        },
        "&.Mui-focused": {
            "& .MuiOutlinedInput-notchedOutline": {
                border: "2px solid #939597",
                borderRadius: "4px",
            },
            "&.Mui-error": {
                "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: '2px solid #E04A57'
                }
            }
        },
        "& .MuiInputBase-input": {
            padding: "0px",
        },
    },
});

const NoDataComponent = ({ heading, msg }: { heading: string, msg: string }) => {
    return (
        <Box sx={styles.emptyComponentWrapper}>
            <Typography sx={styles.emptyText}>{heading}</Typography>
            <Box mt={"8px"}>
                <Typography sx={styles.emptyText}>
                    {msg}
                </Typography>
            </Box>
        </Box>
    );
};

export default function AutoSearch(props: IAutoSearchProps) {
    const {
        options,
        noDataObj = { heading: 'Not Found', msg: '' },
        sx,
        label,
        inputStyle,
        labelStyle,
        required,
        name,
        control,
        defaultValue,
        error
    } = props;
    const isMobile = useMediaQuery(theme.breakpoints.down(950))

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            render={({ field }: any) => {
                return (
                    <Autocomplete
                        {...field}
                        className={'customScrollContainer'}
                        disablePortal
                        popupIcon={
                            <ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />
                        }
                        slotProps={{
                            listbox: {
                                sx: {
                                    ...theme?.typography?.components?.menuLabel,
                                    maxHeight: 34 * 5,
                                    "& .MuiAutocomplete-option": {
                                        "&:hover": {
                                            backgroundColor: "#F6F9FB",
                                        },
                                        "&[aria-selected='true']": {
                                            backgroundColor: "#F6F9FB",
                                        },
                                    },
                                }
                            }
                        }}
                        id="auto-complete-select"
                        options={options}
                        noOptionsText={<NoDataComponent heading={noDataObj.heading} msg={noDataObj.msg} />}
                        getOptionLabel={(option: { label: string, id: string }) => {
                            return option?.label || ''
                        }}
                        onChange={(_, selectedOption: { label: string, id: string }) => {
                            if (selectedOption) {
                                field.onChange(selectedOption.label);
                            } else {
                                field.onChange("")
                            }
                        }}
                        value={options.find((option) => option.label === field.value) || null}
                        sx={{
                            ...autocompleteFieldStyle('307px', '40px'),
                            ...sx,
                            '& + .MuiAutocomplete-popper': {
                                '& .MuiPaper-root': {
                                    boxShadow: "0px 3px 14px 2px rgba(37, 37, 37, 0.12), 0px 8px 10px 1px rgba(37, 37, 37, 0.14), 0px 5px 5px -3px rgba(0, 0, 2, 0.20)",
                                    borderRadius: '12px',
                                    mb: "1rem"
                                },
                                '& .MuiAutocomplete-listbox': {
                                    scrollbarWidth: "thin",
                                    scrollbarColor: `${theme.palette.primary.main} #fff`,
                                    '&::-webkit-scrollbar': {
                                        width: "8px",
                                        borderRadius: '100px'
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: theme.palette.primary.main,
                                        borderRadius: "4px",
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        backgroundColor: "#fff",
                                    },
                                },
                                '& .MuiAutocomplete-option[aria-selected="true"]': {
                                    backgroundColor: "#CECBCA4D !important"
                                },
                                '& .MuiAutocomplete-option:hover': {
                                    backgroundColor: "#F6F9FB !important"
                                },
                            }
                        }}
                        renderInput={(params) => {
                            return (
                                <>
                                    {label ? <InputLabel
                                        sx={{
                                            ...labelStyle,
                                            ...theme.typography.components.inputLabel,
                                            textAlign: "left",
                                            '& .MuiFormLabel-asterisk': {
                                                color: theme?.palette?.error?.main,
                                                position: 'relative',
                                                left: isMobile ? '-1px' : '-4px',
                                            }
                                        }}
                                        required={required}>
                                        {label}
                                    </InputLabel> : null}
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        slotProps={{
                                            htmlInput: {
                                                ...params.inputProps,
                                                readOnly: true
                                            }
                                        }}
                                        sx={{
                                            ...styles.inputTextStyle,
                                            backgroundColor: "#FFFFFF",
                                            pt: .5,
                                            "& input": {
                                                userSelect: "none",
                                                WebkitUserSelect: "none",
                                                caretColor: "transparent"
                                            },
                                            ...inputStyle
                                        }}
                                        error={!!error}
                                        helperText={error?.message || ''}
                                    />
                                </>
                            )
                        }}
                    />
                )
            }}
        />
    )
}